import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Filter, FilterX, Check, MousePointerClick } from 'lucide-react';
import { EmptyState, IconButton, PageMotion, RollingNumber, Scroller } from '@/shared/ui';
import { TicketList, type TicketListSnapshot } from '@/widgets/TicketList';
import { markScrolled } from '@/features/banners/model/scrolled';
import { DetailBar } from '@/widgets/DetailBar';
import { ToolbarSearch } from '@/widgets/ToolbarSearch';
import { useTicketsLive, useTicketsStore } from '@/features/tickets';
import { usePendingApprovalsStore } from '@/features/approvals';
import { TicketDetailRequester } from './TicketDetailRequester';
import { DraftsSection } from './DraftsSection';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import { useUrlParam } from '@/shared/lib/useUrlParam';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { scrollAppToTop, usePageScroll } from '@/shared/lib/pageScroll';
import { useFocusTrap } from '@/shared/lib/useFocusTrap';
import { categoriesApi } from '@/shared/api/categories';
import { categoryIcon, type Category } from '@/entities/category';
import { cn } from '@/shared/lib/cn';
import styles from './MyTicketsPortal.module.css';

export const MyTicketsPortalPage: React.FC<{ mode?: 'mine' | 'approvals' }> = ({
  mode = 'mine',
}) => {
  const navigate = useNavigate();
  const { id: selectedId = null } = useParams<{ id: string }>();
  const total = useTicketsStore((s) => s.total);
  const basePath = mode === 'approvals' ? '/approvals' : '/tickets';
  const [qUrl, setQUrl] = useUrlParam<string>('q', '');
  const [allParam, setAllParam] = useUrlParam<'1' | ''>('all', '');
  const showAll = allParam === '1';
  const [categoriesParam, setCategoriesParam] = useUrlParam<string>('categories', '');
  const categoryIds = useMemo(
    () => (categoriesParam ? categoriesParam.split(',') : []),
    [categoriesParam],
  );
  const [search, setSearch] = useState(qUrl);
  const [categories, setCategories] = useState<Category[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchPinned = useMediaQuery('(max-width: 768px)');
  const [draftIds, setDraftIds] = useState<string[]>([]);
  const filtersRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const pageScroll = usePageScroll();
  const showList = !(pageScroll && selectedId);
  const showDetail = !pageScroll || Boolean(selectedId);
  const listMemory = useRef<TicketListSnapshot | null>(null);
  useTicketsLive();
  const detailRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (selectedId) scrollAppToTop(pageScroll, detailRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);
  const onDetailScroll = useCallback(() => {
    markScrolled(detailRef.current?.scrollTop ?? 0);
  }, []);
  const q = useDebouncedValue(search.trim(), 350);
  const lastPushedQRef = useRef(qUrl);
  useEffect(() => {
    if (qUrl !== lastPushedQRef.current) {
      lastPushedQRef.current = qUrl;
      setSearch(qUrl);
    }
  }, [qUrl]);
  useEffect(() => {
    if (q !== search.trim()) return;
    if (q === qUrl) return;
    lastPushedQRef.current = q;
    setQUrl(q);
  }, [q, search, qUrl, setQUrl]);

  const [, setSearchParams] = useSearchParams();
  const patchUrlParams = useCallback(
    (patch: Record<string, string>) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v) p.set(k, v);
            else p.delete(k);
          }
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useFocusTrap(popoverRef, filtersOpen);

  useEffect(() => {
    categoriesApi
      .list()
      .then((res) => setCategories(res.data ?? []))
      .catch(() => setCategories([]));
  }, []);

  const refreshApprovals = usePendingApprovalsStore((s) => s.refresh);
  useEffect(() => {
    void refreshApprovals();
  }, [refreshApprovals, selectedId]);

  const { search: locationSearch } = useLocation();
  const legacyApprovals = locationSearch.includes('mode=approvals');
  useEffect(() => {
    if (mode === 'mine' && legacyApprovals) {
      navigate(
        (selectedId ? `/approvals/${selectedId}` : '/approvals') +
          locationSearch.replace(/([?&])mode=approvals&?/, '$1').replace(/[?&]$/, ''),
        { replace: true },
      );
    }
  }, [mode, legacyApprovals, locationSearch, selectedId, navigate]);

  useEffect(() => {
    if (!filtersOpen) return;
    const onDown = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setFiltersOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  const params = {
    view: mode,
    ...(q ? { q } : {}),
    ...(categoryIds.length ? { categoryIds } : {}),
    ...(showAll ? { includeClosed: true } : {}),
  };

  const hasFilters = showAll || categoryIds.length > 0 || Boolean(q);
  const reset = () => {
    patchUrlParams({ q: '', all: '', categories: '' });
    lastPushedQRef.current = '';
    setDraftIds([]);
    setSearch('');
    setSearchOpen(false);
    setFiltersOpen(false);
  };

  const openFilters = () => {
    setDraftIds(categoryIds);
    setFiltersOpen((v) => !v);
  };
  const toggleDraft = (id: string) =>
    setDraftIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const applyFilters = () => {
    setCategoriesParam(draftIds.join(','));
    setFiltersOpen(false);
  };

  const emptyTitle = hasFilters
    ? 'Ничего не найдено'
    : mode === 'approvals'
      ? 'Согласований нет'
      : 'Нет открытых обращений';
  const emptyHint = hasFilters
    ? 'Измените фильтры или поисковый запрос'
    : mode === 'approvals'
      ? 'Здесь появятся заявки, где вас назначили согласующим'
      : 'Здесь только открытые обращения. Закрытые — во вкладке «Все»';

  return (
    <PageMotion className={styles.pageFill}>
      <main className={styles.page}>
        {showList && (
          <section className={styles.list} data-list-column>
            <div className={styles.listHead}>
              <h1 className={styles.title}>
                {mode === 'approvals' ? 'Согласования' : 'Обращения'}
              </h1>
              <RollingNumber className={styles.count} value={total} />
            </div>

            { }
            <div className={styles.toolbar}>
              <div className={styles.tabs} role="tablist" aria-label="Какие заявки показывать" data-tour="list-tabs">
                {[
                  { all: false, label: 'Открытые' },
                  { all: true, label: 'Все' },
                ].map((t) => (
                  <button
                    type="button"
                    key={t.label}
                    role="tab"
                    aria-selected={showAll === t.all}
                    className={cn(styles.tabBtn, showAll === t.all && styles.tabBtnActive)}
                    onClick={() => setAllParam(t.all ? '1' : '')}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className={styles.tools}>
                <div className={styles.actions}>
                  <ToolbarSearch
                    value={search}
                    onChange={setSearch}
                    open={searchOpen}
                    onOpenChange={setSearchOpen}
                    pinned={searchPinned}
                    label={mode === 'approvals' ? 'Поиск по согласованиям' : 'Поиск по обращениям'}
                    placeholder="Тема, описание или номер"
                    tooltip="Поиск по теме, описанию или номеру"
                  />

                  <div className={styles.filtersAnchor} ref={filtersRef}>
                    <IconButton
                      data-tour="list-filters"
                      label="Фильтры"
                      tooltip="Категории"
                      size="lg"
                      aria-expanded={filtersOpen}
                      className={cn(filtersOpen && styles.actionOn)}
                      onClick={openFilters}
                    >
                      <Filter size={18} aria-hidden />
                      {categoryIds.length > 0 && (
                        <span className={styles.filtersBadge}>{categoryIds.length}</span>
                      )}
                    </IconButton>

                    {filtersOpen && (
                      <div ref={popoverRef} className={styles.popover} role="dialog" aria-label="Фильтры">
                        <div className={styles.popoverHead}>Фильтры</div>
                        <Scroller className={styles.popoverList}>
                          {categories.map((c) => {
                            const Icon = categoryIcon(c.icon);
                            const checked = draftIds.includes(c.id);
                            return (
                              <button
                                type="button"
                                key={c.id}
                                className={cn(styles.optionRow, checked && styles.optionRowActive)}
                                onClick={() => toggleDraft(c.id)}
                                role="checkbox"
                                aria-checked={checked}
                              >
                                <span className={cn(styles.checkbox, checked && styles.checkboxOn)}>
                                  {checked && <Check size={13} strokeWidth={3} aria-hidden />}
                                </span>
                                <Icon size={18} aria-hidden className={styles.optionIcon} />
                                <span className={styles.optionLabel}>{c.name}</span>
                              </button>
                            );
                          })}
                        </Scroller>
                        <div className={styles.popoverFoot}>
                          <button
                            type="button"
                            className={styles.popoverReset}
                            onClick={() => setDraftIds([])}
                            disabled={draftIds.length === 0}
                          >
                            Сбросить
                          </button>
                          <button type="button" className={styles.popoverApply} onClick={applyFilters}>
                            Применить
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {hasFilters && (
                    <IconButton
                      label="Сбросить фильтры и поиск"
                      size="lg"
                      tone="danger"
                      className={styles.actionReset}
                      onClick={reset}
                    >
                      <FilterX size={18} aria-hidden />
                    </IconButton>
                  )}
                </div>
              </div>
            </div>

            { }
            {mode === 'mine' && <DraftsSection />}

            <div className={styles.listScroll}>
              { }
              <TicketList
                params={params}
                onSelect={(tid) => navigate(`${basePath}/${tid}${locationSearch}`)}
                selectedId={selectedId}
                emptyTitle={emptyTitle}
                emptyHint={emptyHint}
                scrollMemory={listMemory}
              />
            </div>
          </section>
        )}

        {showDetail && (
          <Scroller
            element="section"
            className={styles.detail}
            viewportClassName={styles.detailViewport}
            viewportRef={detailRef}
            onScroll={onDetailScroll}
            page={pageScroll}
          >
            {selectedId ? (
              <>
                { }
                <div className={styles.detailBar}>
                  <DetailBar backTo={`${basePath}${locationSearch}`} backLabel="К обращениям" />
                </div>
                { }
                <TicketDetailRequester key={selectedId} ticketId={selectedId} />
              </>
            ) : (
              <div className={styles.empty}>
                <EmptyState
                  Icon={MousePointerClick}
                  title={mode === 'approvals' ? 'Согласование не выбрано' : 'Обращение не выбрано'}
                  hint={
                    mode === 'approvals' ? (
                      'Выберите заявку из списка слева, чтобы одобрить или отклонить'
                    ) : (
                      <>
                        Выберите обращение из списка слева или{' '}
                        <Link to="/categories" className={styles.hintLink}>
                          создайте новое
                        </Link>
                      </>
                    )
                  }
                />
              </div>
            )}
          </Scroller>
        )}
      </main>
    </PageMotion>
  );
};
